<template>
  <Teleport to="body">
    <div class="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto py-10 px-4">
      <div class="fixed inset-0 bg-black/60" @click="$emit('close')" />
      <div class="relative bg-surface dark:bg-surface-dark rounded-xl shadow-2xl w-full max-w-3xl">
        <div class="px-6 pt-6 pb-4 border-b border-stroke dark:border-stroke-dark">
          <h2 class="text-lg font-semibold text-fg-base dark:text-fg-base-dark">Invoice</h2>
          <p class="text-sm text-fg-muted dark:text-fg-muted-dark mt-0.5">
            {{ contact.name }} · a PDF for their records — doesn't charge or change billing
          </p>
        </div>

        <div v-if="loading" class="px-6 py-8 text-sm text-fg-muted dark:text-fg-muted-dark">Loading…</div>
        <div v-else class="px-6 py-5 space-y-5">
          <!-- Past invoices -->
          <div v-if="invoices.length">
            <p :class="LABEL">Previous invoices</p>
            <div class="divide-y divide-stroke dark:divide-stroke-dark border border-stroke dark:border-stroke-dark rounded-lg">
              <div v-for="inv in invoices" :key="inv.id" class="flex items-center gap-3 px-3 py-2 text-sm">
                <span class="font-medium text-fg-base dark:text-fg-base-dark">{{ inv.label }}</span>
                <span class="text-fg-muted dark:text-fg-muted-dark">{{ fmtDate(inv.issue_date) }} · {{ fmtCents(inv.total_cents) }} · {{ inv.status === 'paid' ? 'paid' : 'due' }}</span>
                <span class="text-xs text-fg-faint dark:text-fg-faint-dark flex-1 truncate">{{ inv.sent_at ? `emailed ${fmtDate(inv.sent_at)}` : 'not emailed' }}</span>
                <a :href="`${API}/api/manual-invoices/${inv.id}/pdf`" target="_blank" class="text-xs text-blue-600 dark:text-blue-400 hover:underline">PDF</a>
                <button v-if="contact.email" :disabled="!!busy" class="text-xs text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-50" @click="resend(inv)">
                  {{ busy === `resend-${inv.id}` ? 'Sending…' : 'Email again' }}
                </button>
              </div>
            </div>
          </div>

          <div class="grid grid-cols-2 gap-3">
            <div class="row-span-2">
              <label :class="LABEL">Bill to</label>
              <textarea v-model="form.bill_to" rows="4" :class="INPUT" />
            </div>
            <div class="grid grid-cols-2 gap-3">
              <div>
                <label :class="LABEL">Issued</label>
                <input v-model="form.issue_date" type="date" :class="INPUT" />
              </div>
              <div>
                <label :class="LABEL">Status</label>
                <select v-model="form.status" :class="INPUT">
                  <option value="due">Amount due</option>
                  <option value="paid">Paid</option>
                </select>
              </div>
            </div>
            <div v-if="form.status === 'due'">
              <label :class="LABEL">Due date (optional)</label>
              <input v-model="form.due_date" type="date" :class="INPUT" />
            </div>
          </div>

          <!-- Prefill from lessons -->
          <div class="bg-surface-muted dark:bg-surface-muted-dark rounded-lg px-3 py-3">
            <p :class="LABEL">Add lessons from</p>
            <div class="flex items-center gap-2">
              <input v-model="range.start" type="date" :class="INPUT" />
              <span class="text-sm text-fg-muted dark:text-fg-muted-dark">to</span>
              <input v-model="range.end" type="date" :class="INPUT" />
              <button :disabled="!!busy || !range.start || !range.end" @click="loadLessons"
                class="shrink-0 px-3 py-2 text-sm border border-stroke dark:border-stroke-dark rounded-lg text-fg-secondary dark:text-fg-secondary-dark hover:bg-surface dark:hover:bg-surface-dark disabled:opacity-50">
                {{ busy === 'lessons' ? 'Loading…' : 'Add' }}
              </button>
            </div>
            <p v-if="lessonsNote" class="text-xs text-fg-muted dark:text-fg-muted-dark mt-1.5">{{ lessonsNote }}</p>
          </div>

          <!-- Lines -->
          <div>
            <p :class="LABEL">Lines</p>
            <div class="space-y-1.5">
              <div v-for="(ln, i) in lines" :key="ln.key" class="flex items-center gap-2" :class="{ 'opacity-50': !ln.checked }">
                <input v-model="ln.checked" type="checkbox" class="shrink-0" title="Include on the invoice" />
                <input v-model="ln.date" type="date" :class="[FIELD, 'w-36 shrink-0']" />
                <input v-model="ln.description" placeholder="Description" :class="[FIELD, 'flex-1 min-w-0']" />
                <input v-model="ln.qty" type="number" min="1" step="1" title="Quantity" :class="[FIELD, 'w-16 shrink-0 text-right']" />
                <span class="shrink-0 text-xs text-fg-faint dark:text-fg-faint-dark">×</span>
                <input v-model="ln.rate" type="number" step="0.01" placeholder="Rate" title="Rate (each)" :class="[FIELD, 'w-24 shrink-0 text-right']" />
                <span class="w-20 shrink-0 text-right text-sm text-fg-base dark:text-fg-base-dark">{{ fmtCents(lineCents(ln)) }}</span>
                <span class="w-14 shrink-0 text-xs text-fg-faint dark:text-fg-faint-dark" :title="ln.hint">{{ ln.hint }}</span>
                <button class="shrink-0 text-fg-faint hover:text-red-500" title="Remove" @click="lines.splice(i, 1)">
                  <XMarkIcon class="h-4 w-4" />
                </button>
              </div>
            </div>
            <p class="text-xs text-fg-faint dark:text-fg-faint-dark mt-1.5">Date is optional — e.g. “Lessons (30 min)”, 8 × $42.50. Qty and Rate print only when a quantity is above 1.</p>
            <div class="flex items-center justify-between mt-2">
              <button class="text-xs text-blue-600 dark:text-blue-400 hover:underline" @click="addLine()">+ Add a line</button>
              <span class="text-sm font-semibold text-fg-base dark:text-fg-base-dark">
                {{ form.status === 'paid' ? 'Total paid' : 'Amount due' }}: {{ fmtCents(totalCents) }}
              </span>
            </div>
          </div>

          <div>
            <label :class="LABEL">Note on the invoice (optional)</label>
            <textarea v-model="form.note" rows="2" placeholder="e.g. Please make checks payable to…" :class="INPUT" />
          </div>

          <p v-if="error" class="text-red-500 dark:text-red-400 text-sm">{{ error }}</p>
          <p v-if="done" class="text-green-600 dark:text-green-400 text-sm">{{ done }}</p>

          <div class="flex flex-wrap gap-2 pt-1">
            <button @click="$emit('close')" :class="SECONDARY">Close</button>
            <span class="flex-1" />
            <button :disabled="!canSave || !!busy" @click="preview" :class="SECONDARY">
              {{ busy === 'preview' ? 'Building…' : 'Preview' }}
            </button>
            <button :disabled="!canSave || !!busy" @click="save(false)" :class="SECONDARY">
              {{ busy === 'save' ? 'Saving…' : 'Save & open PDF' }}
            </button>
            <button :disabled="!canSave || !!busy || !contact.email" @click="save(true)"
              :title="contact.email ? `Emails ${contact.email} with the PDF attached` : 'No email on file'"
              class="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium disabled:opacity-50 transition-colors">
              {{ busy === 'send' ? 'Sending…' : 'Save & email' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
// Manual invoice (api/endpoints/manual_invoices.py): lines prefilled from the
// family's lessons, edited freely, saved with a number, emailed as a PDF attachment.
import { ref, computed, onMounted } from 'vue'
import { XMarkIcon } from '@heroicons/vue/24/outline'
import { useApi } from '../../composables/useApi.js'

const props = defineProps({ contact: { type: Object, required: true } })
defineEmits(['close'])

const { fetchApi } = useApi()
const API = import.meta.env.VITE_API_BASE_URL || ''
const LABEL = 'block text-xs font-medium text-fg-muted dark:text-fg-muted-dark mb-1'
// FIELD has no width, so a line's w-36/w-16/w-24 apply (w-full would beat them)
const FIELD = 'px-2.5 py-1.5 text-sm rounded-lg border border-stroke dark:border-stroke-dark bg-surface dark:bg-surface-dark text-fg-base dark:text-fg-base-dark'
const INPUT = `w-full ${FIELD}`
const SECONDARY = 'px-4 py-2 text-sm border border-stroke dark:border-stroke-dark rounded-lg text-fg-secondary dark:text-fg-secondary-dark hover:bg-surface-muted dark:hover:bg-surface-muted-dark disabled:opacity-50 transition-colors'

const ymd = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
const today = new Date()

const loading = ref(true)
const busy = ref(null)
const error = ref('')
const done = ref('')
const invoices = ref([])
const lessonsNote = ref('')
const form = ref({
  bill_to: '', issue_date: ymd(today), status: 'due',
  due_date: ymd(new Date(today.getFullYear(), today.getMonth(), today.getDate() + 14)), note: '',
})
const range = ref({ start: ymd(new Date(today.getFullYear(), today.getMonth() - 1, 1)), end: ymd(new Date(today.getFullYear(), today.getMonth(), 0)) })
const lines = ref([])
let nextKey = 0

function addLine(ln = {}) {
  lines.value.push({ key: nextKey++, checked: true, date: '', description: '', qty: 1, rate: '', hint: '', ...ln })
}

const fmtCents = c => `${c < 0 ? '-' : ''}$${(Math.abs(c) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
const fmtDate = s => new Date(s.length === 10 ? `${s}T12:00:00` : s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'America/New_York' })
const cents = v => Math.round(parseFloat(v || 0) * 100)

const chosen = computed(() => lines.value.filter(l => l.checked && l.description.trim()))
const qtyOf = l => Math.max(1, parseInt(l.qty) || 1)
const lineCents = l => qtyOf(l) * cents(l.rate)
const totalCents = computed(() => chosen.value.reduce((s, l) => s + lineCents(l), 0))
const canSave = computed(() => form.value.bill_to.trim() && chosen.value.length)

function body(send = false) {
  return JSON.stringify({
    contact_id: props.contact.id, ...form.value, due_date: form.value.due_date || null, send,
    lines: chosen.value.map(l => ({ date: l.date || null, description: l.description.trim(), quantity: qtyOf(l), unit_cents: cents(l.rate) })),
  })
}

async function loadInvoices() {
  const r = await fetchApi(`/api/manual-invoices/contact/${props.contact.id}`)
  invoices.value = r.invoices
  return r
}

onMounted(async () => {
  try {
    form.value.bill_to = (await loadInvoices()).bill_to
  } catch (e) {
    error.value = e.message || 'Failed to load.'
  } finally {
    loading.value = false
  }
})

async function run(key, fn) {
  busy.value = key
  error.value = ''
  done.value = ''
  try {
    await fn()
  } catch (e) {
    error.value = e.message || 'Something went wrong.'
  } finally {
    busy.value = null
  }
}

const loadLessons = () => run('lessons', async () => {
  const rows = await fetchApi(`/api/manual-invoices/contact/${props.contact.id}/lessons?start=${range.value.start}&end=${range.value.end}`)
  lines.value = lines.value.filter(l => l.description.trim() || l.rate)
  for (const r of rows) {
    addLine({ checked: r.checked, date: r.date, description: r.description, rate: (r.amount_cents / 100).toFixed(2),
      hint: r.attendance || (r.paid ? 'paid' : '') })
  }
  const skipped = rows.filter(r => !r.checked).length
  lessonsNote.value = rows.length
    ? `Added ${rows.length} lesson${rows.length === 1 ? '' : 's'}${skipped ? ` — ${skipped} unchecked (excused or not billed)` : ''}.`
    : 'No lessons in that range.'
})

// The PDF tab is opened before the request so the browser doesn't treat it as a popup.
async function openPdf(fetchBlob) {
  const tab = window.open('', '_blank')
  try {
    const blob = await fetchBlob()
    if (tab) tab.location = URL.createObjectURL(blob)
  } catch (e) {
    tab?.close()
    throw e
  }
}

const preview = () => run('preview', () => openPdf(async () => {
  const res = await fetch(`${API}/api/manual-invoices/preview`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: body() })
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || `Preview failed (${res.status})`)
  return res.blob()
}))

const save = send => run(send ? 'send' : 'save', async () => {
  const tab = send ? null : window.open('', '_blank')
  try {
    const inv = await fetchApi('/api/manual-invoices', { method: 'POST', body: body(send) })
    if (tab) tab.location = `${API}/api/manual-invoices/${inv.id}/pdf`
    done.value = send ? `${inv.label} saved and emailed to ${props.contact.email}.` : `${inv.label} saved.`
  } catch (e) {
    tab?.close()
    throw e
  } finally {
    await loadInvoices().catch(() => {})
  }
  lines.value = []
  lessonsNote.value = ''
})

const resend = inv => run(`resend-${inv.id}`, async () => {
  if (!confirm(`Email ${inv.label} to ${props.contact.email} again?`)) return
  await fetchApi(`/api/manual-invoices/${inv.id}/send`, { method: 'POST' })
  done.value = `${inv.label} emailed to ${props.contact.email}.`
  await loadInvoices()
})
</script>
